import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { UrlService } from '../shared/services/url/url.service';
import { Document } from './document.model';
import {
  ALL_YEARS,
  DocumentTypesEnum,
  ISendDocumentPayload,
} from './document-data.data';
import { Settings } from '../settings-data/settings.model';
import {
  COMMON_FIELDS_KEYS,
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { Header } from '../entity-data/sub-models/header.model';
import { fromJS, List, Map } from 'immutable';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { has, isBoolean, isNumber, keys, reduce } from 'lodash';
import { CalculatorService } from '../shared/services/calculator/calculator.service';
import { IDocumentDataBuilder } from './document-data-builder.interface';
import {
  ALL_LIST_FILTER,
  ISearchParams,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import { UtilsService } from '../shared/services/utils/utils.service';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { Tax } from '../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentItem } from './sub-models/document-item.model';
import { AppliedTax } from './sub-models/applied-tax.model';
import { Discount } from './sub-models/discount.model';
import { TaxRate } from '../settings-data/sub-models/company-settings/tax-rate.model';
import { PaymentsSettings } from '../settings-data/sub-models/company-settings/payments.model';
import { CalculationResult } from './sub-models/calculation-result.model';
import { IRendererOptions } from '../renderer/renderer.interface';
import { serialize } from '../../utils/helpers/model.helper';
import { EmailService } from '../shared/services/email/email.service';
import { MoneyPipe } from '../shared/pipes/money/money.pipe';
import { FriendlyDatePipe } from '../shared/pipes/friendly-date/friendly-date.pipe';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { DateTimeService, MOMENT_TIMEZONELESS_DATE_FORMAT } from 'i2g-ng-utils';

export abstract class DocumentDataBuilder<T extends Document>
  extends EntityDataBuilder<T>
  implements IDocumentDataBuilder {
  documentType: DocumentTypesEnum;
  featureName: string;
  depositFeatureName: string;
  yearFilterKey: string = SearchParamsEnum.document_tax_year;
  refreshFiltersDelay = 0;

  constructor(
    protected dateTimeService: DateTimeService,
    protected calculatorService: CalculatorService,
    protected utilsService: UtilsService,
    protected emailService: EmailService,
    protected translateService: TranslateService,
    protected moneyPipe: MoneyPipe,
    protected friendlyDatePipe: FriendlyDatePipe,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings?: Settings,
    isDirty?: boolean,
    isNew?: boolean,
    recalculate?: boolean
  ): T {
    const settingsKey = Document.CONTENT_KEYS.SETTINGS;
    const docDateKey = Document.CONTENT_KEYS.DOC_DATE;
    const companyInfoKey = Document.CONTENT_KEYS.COMPANY_INFO;
    const withholdingTaxKey = Document.CONTENT_KEYS.WITHHOLDING_TAX;
    const taxesKey = Document.CONTENT_KEYS.TAXES;
    const headerTypeKey = [COMMON_FIELDS_KEYS.HEADER, Header.KEYS.TYPE];

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const document: Document = new this.model(
      props.withMutations((map) => {
        // default document type
        if (!map.getIn(headerTypeKey)) {
          map.setIn(headerTypeKey, this.documentType);
        }

        // default doc date
        if (!getInContent(map, docDateKey)) {
          setInContent(map, docDateKey, this.dateTimeService.getDateString());
        }

        if (settings) {
          // Default settings
          let documentSettings: DocumentPresetSettings =
            settings.documentPresetSettings;

          // merge current settings
          if (getInContent(map, settingsKey)) {
            documentSettings = documentSettings.merge(
              getInContent(map, settingsKey)
            ) as DocumentPresetSettings;
          }

          setInContent(map, settingsKey, documentSettings);

          // default company info
          if (!getInContent(map, companyInfoKey)) {
            setInContent(map, companyInfoKey, settings.companyInfo);
          }

          // default withholding tax
          if (!getInContent(map, withholdingTaxKey)) {
            setInContent(map, withholdingTaxKey, settings.withholdingTax);
          }

          // default taxes
          if (!getInContent(map, taxesKey)) {
            setInContent(map, taxesKey, settings.taxes);
          }
        }
      })
    );

    const docProps = recalculate
      ? this.calculatorService.getCalculations(document)
      : document;
    const calculatedDocProps = docProps.withMutations((map) => {
      if (isBoolean(isDirty) || document.isDirty()) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew) || document.isNew()) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new this.model(calculatedDocProps);
  }

  buildEntities(entitiesProps: any[]): List<T> {
    return List<T>(
      entitiesProps ? entitiesProps.map(this.buildEntity.bind(this)) : []
    );
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    _sortOrder: SortOrderEnum
  ): (a: T, b: T) => number {
    const order = _sortOrder === SortOrderEnum.asc ? 1 : -1;

    return (a: T, b: T): number => {
      let sortValue = 0;

      switch (sortBy) {
        case SortByEnum.doc_number:
          sortValue = this.docNumberComparatorFn(a, b);
          break;
        case SortByEnum.billing_name:
          sortValue =
            this.billingNameComparatorFn(a, b) || this.emailComparatorFn(a, b);
          break;
        case SortByEnum.doc_date:
          sortValue =
            this.docDateComparatorFn(a, b) ||
            this.billingNameComparatorFn(a, b) ||
            this.emailComparatorFn(a, b) ||
            this.docNumberComparatorFn(a, b);
          break;
      }

      return sortValue * order;
    };
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings,
    toggles?: Toggles
  ): (entity: T) => boolean {
    const year = +searchParams[this.yearFilterKey];

    if (year && year !== ALL_YEARS) {
      const taxYearStart = settings.companySettings.taxYearStart;

      return (document: T) =>
        this.utilsService.isInTaxYear(taxYearStart, year, document.docDate);
    }

    return () => true;
  }

  buildListFilter(entity: T, settings?: Settings, toggles?: Toggles): string {
    return ALL_LIST_FILTER;
  }

  validateSearchParams(searchParams: ISearchParams, filter: string): boolean {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) =>
        [
          SortByEnum.doc_number,
          SortByEnum.billing_name,
          SortByEnum.doc_date,
        ].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
      [SearchParamsEnum.document_tax_year]: isNumber,
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }

  /*** Common document builders ***/
  buildDocumentItem(
    props: any,
    taxes: List<Tax>,
    withholdingTax: WithholdingTax,
    disableAutoTax?: boolean
  ): DocumentItem {
    const appliedTaxesKey = DocumentItem.KEYS.APPLIED_TAXES;
    const withholdingTaxAppliesKey = DocumentItem.KEYS.WITHHOLDING_TAX_APPLIES;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const appliedTaxesProps = props.get(appliedTaxesKey);

    if (!appliedTaxesProps) {
      const appliedTaxes = List([]).withMutations((list) => {
        (taxes || List([]))
          .filter((tax: Tax) => tax.autoApply && !disableAutoTax)
          .forEach((tax: Tax) => {
            list.push(
              fromJS({
                [AppliedTax.KEYS.TAX_ID]: tax.id,
                [AppliedTax.KEYS.RATE]: tax.firstRate,
              })
            );
          });
      });

      props = props.set(appliedTaxesKey, appliedTaxes);
    }

    if (props.get(withholdingTaxAppliesKey, null) === null) {
      props = props.set(withholdingTaxAppliesKey, withholdingTax.enabled);
    }

    return new DocumentItem(props);
  }

  buildDiscount(props?: any): Discount {
    return new Discount(props);
  }

  buildUpdatedDocumentItemsWithTax(
    document: Document,
    tax: Tax
  ): Map<string, any> {
    const newRates = tax.rates
      .map((taxRate: TaxRate) => taxRate.value)
      .toArray();

    return this.calculatorService.updateTaxRates(document, tax.id, newRates);
  }

  buildDocumentSettingsPropsForCardPayments(
    cardPaymentsEnabled: boolean
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.SETTINGS]: {
        [DocumentPresetSettings.KEYS
          .CARD_PAYMENTS_DISABLED]: !cardPaymentsEnabled,
      },
    });
  }

  buildDocumentContentPropsWithNoDeposit(): Map<string, any> {
    return fromJS({ [Document.CONTENT_KEYS.DEPOSIT]: null });
  }

  buildSendDocumentPayload({
    document,
    settings,
    accountEmail,
    bankTransferFeatureEnabled,
    cardPaymentsFeatureEnabled,
    paypalFeatureEnabled,
    messageDeliveryData,
    hasTrackingFeature,
    emailTemplateExperiment,
  }: ISendDocumentPayload): any {
    const paymentsSettings: PaymentsSettings = settings.payments;
    const docSettings: DocumentPresetSettings = document.settings;
    const calculationResult: CalculationResult = document.calculationResult;
    const hasBalanceDue: boolean = calculationResult.outstandingBalance > 0;
    const bankTransferEnabled: boolean =
      paymentsSettings.bankTransferEnabled() &&
      !docSettings.bankTransferDisabled;
    const cardPaymentsEnabled: boolean =
      paymentsSettings.cardPaymentsEnabled() &&
      !docSettings.cardPaymentsDisabled;
    const paypalEcEnabled: boolean =
      paymentsSettings.paypalEcEnabled && !docSettings.paypalEcDisabled;
    const emailMessage: string = messageDeliveryData.message.replace(
      /[\n]/g,
      '<br/>'
    );
    const bankTransferGloballyEnabled: boolean =
      bankTransferFeatureEnabled && bankTransferEnabled;
    const cardPaymentsGloballyEnabled: boolean =
      cardPaymentsFeatureEnabled && cardPaymentsEnabled;
    const paypalEcGloballyEnabled: boolean =
      paypalFeatureEnabled && paypalEcEnabled;
    const hasEPayments: boolean =
      bankTransferGloballyEnabled ||
      cardPaymentsGloballyEnabled ||
      paypalEcGloballyEnabled;
    const renderingOptions: IRendererOptions = {
      disable_pay_now_link: false,
      bank_transfer_globally_enabled: bankTransferGloballyEnabled,
      card_payments_globally_enabled: cardPaymentsGloballyEnabled,
      paypal_ec_globally_enabled: paypalEcGloballyEnabled,
    };
    const documentType: DocumentTypesEnum = document.documentType;

    let emailSubject: string = messageDeliveryData.subject;
    let superSubject: string =
      hasBalanceDue &&
      hasEPayments &&
      documentType === DocumentTypesEnum.invoice
        ? this._buildSuperSubject(
            cardPaymentsGloballyEnabled,
            paypalEcGloballyEnabled,
            document
          )
        : null;

    // experiment new invoice email template
    const templateId =
      emailTemplateExperiment && // check feature toggle
      documentType === DocumentTypesEnum.invoice && // check if its invoice
      !document.deposit // check if there is deposit request
        ? environment.invoiceEmailTemplateId
        : environment.documentEmailTemplateId;

    /**
     * START - Data for experiment
     */
    const currencyCode = document.settings.currencyCode;
    const businessName = document.companyInfo.name || accountEmail;
    const balanceDue = calculationResult.outstandingBalance;
    const totalAmount = calculationResult.total;
    const isFullyPaid = calculationResult.payments.isFullyPaid;
    const overdueDays = this.dateTimeService.calculateNumberOfUnitsFromDate(
      this.dateTimeService.getDateString(),
      document.dueDate,
      'day',
      'days',
      false
    );
    const depositDue =
      calculationResult.payments.deposit &&
      calculationResult.payments.deposit.isUnpaid()
        ? calculationResult.payments.deposit.amountRemaining
        : 0;
    const depositOverdueDays =
      depositDue && document.deposit && document.deposit.dueDate
        ? this.dateTimeService.calculateNumberOfUnitsFromDate(
            this.dateTimeService.getDateString(),
            document.deposit.dueDate,
            'day',
            'days',
            false
          )
        : 0;

    if (templateId === environment.invoiceEmailTemplateId) {
      // override the subject and super_subject
      emailSubject = this._buildEmailTemplateExperimentSubject(
        document.docNumber,
        businessName,
        overdueDays > 0,
        depositDue > 0,
        depositOverdueDays > 0,
        isFullyPaid
      );

      superSubject = this._buildEmailTemplateExperimentSuperSubject(
        document.dueDate,
        document.deposit && document.deposit.dueDate,
        depositOverdueDays,
        isFullyPaid
      );
    }
    /**
     * END - Data for experiment
     */

    return {
      document: serialize(document),
      rendering_options: renderingOptions,
      delivery_options: {
        template_id: templateId,
        template_data: {
          // TODO: revert change in email template title once this change goes out
          super_subject: superSubject,
          show_footer: messageDeliveryData.includeFooter,
          language: docSettings.locale.split('-')[0],
          doc_type: documentType,
          has_balance: hasBalanceDue, // TODO: revisit this variable as it's used twice in the
          // template (`has_balance`)
          payment_url: document.links.electronicPaymentUrl,
          payment_notification_url:
            (cardPaymentsFeatureEnabled || paypalFeatureEnabled) &&
            settings.companySettings.emailDefaults.includePaymentNotification
              ? document.links.notifyPaymentUrl
              : null,
          tracking_url: hasTrackingFeature ? document.links.beaconUrl : null,
          subject_line_spamcheck: emailSubject,
          body_spamcheck: emailMessage,
          bank_transfer_globally_enabled: bankTransferGloballyEnabled,
          card_payments_globally_enabled: cardPaymentsGloballyEnabled,
          paypal_ec_globally_enabled: paypalEcGloballyEnabled,

          // will result in an object where the key is the card type and the value is true
          payment_methods: paymentsSettings.availableCardTypes.reduce(
            (result: any, cardType: string) => {
              result[cardType] = true;

              return result;
            },
            {}
          ),

          /**
           * START - Data for experiment
           */
          company_name: businessName,
          invoice_amount: this.moneyPipe.transform(
            isFullyPaid ? totalAmount : balanceDue,
            currencyCode,
            document.settings.locale
          ),
          invoice_fully_paid: isFullyPaid,
          overdue_days: overdueDays,
          days_until_due: overdueDays < 0 ? Math.abs(overdueDays) : 0,
          due_date: this.friendlyDatePipe.transform(document.dueDate),
          deposit_amount: this.moneyPipe.transform(
            depositDue,
            currencyCode,
            document.settings.locale
          ),
          deposit_due_date: depositDue
            ? this.friendlyDatePipe.transform(document.deposit.dueDate)
            : false,
          deposit_overdue_days: depositOverdueDays,
          deposit_days_until_due:
            depositOverdueDays < 0 ? Math.abs(depositOverdueDays) : 0,
          /**
           * END - Data for experiment
           */
        },
        recipients: List(
          this.emailService.tokenizeEmails(messageDeliveryData.to)
        )
          .toSet()
          .toArray(),
        cc_emails: List(
          this.emailService.tokenizeEmails(messageDeliveryData.cc)
        )
          .toSet()
          .toArray(),
        bcc_emails: List(
          this.emailService.tokenizeEmails(messageDeliveryData.bcc)
        )
          .toSet()
          .toArray(),
        subject: emailSubject,
        from_name: document.companyInfo.name || accountEmail,
        reply_to_email: accountEmail,
        file_name: messageDeliveryData.fileName,
      },
    };
  }

  /*** Document comparators ***/
  protected docNumberComparatorFn(a: Document, b: Document): number {
    const getSortableNumber = (document: Document) =>
      document.sortable.docNumber || '';

    return this.sortOrder(getSortableNumber(a), getSortableNumber(b));
  }

  protected billingNameComparatorFn(a: Document, b: Document): number {
    const getBillingName = (document: Document) =>
      (document.billingName || '').toLowerCase();

    return this.sortOrder(getBillingName(a), getBillingName(b));
  }

  protected emailComparatorFn(a: Document, b: Document): number {
    const getBillingEmail = (document: Document) =>
      (document.billingEmail || '').toLowerCase();

    return this.sortOrder(getBillingEmail(a), getBillingEmail(b));
  }

  protected docDateComparatorFn(a: Document, b: Document): number {
    const getDocDate = (document: Document) =>
      this.dateTimeService.getMilliseconds(
        document.docDate,
        MOMENT_TIMEZONELESS_DATE_FORMAT
      );

    return this.sortOrder(getDocDate(a), getDocDate(b));
  }

  protected sortOrder(a: string | number, b: string | number): number {
    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    } else {
      return 0;
    }
  }

  /*** helpers ***/
  private _buildSuperSubject(
    cardPaymentsGloballyEnabled: boolean,
    paypalEcGloballyEnabled: boolean,
    document: Document
  ): string {
    const balanceDue = document.calculationResult.outstandingBalance;
    const currencyCode = document.settings.currencyCode;
    const translateValues = {
      balanceDue: this.moneyPipe.transform(
        balanceDue,
        currencyCode,
        document.settings.locale
      ),
      dueDate: this.friendlyDatePipe.transform(document.dueDate),
    };

    let superSubject = '';

    if (cardPaymentsGloballyEnabled && paypalEcGloballyEnabled) {
      superSubject = `${this.translateService.instant(
        'PAYMENTS.SUPER_SUBJECT.CARDS_AND_PAYPAL'
      )} | `;
    } else if (cardPaymentsGloballyEnabled) {
      superSubject = `${this.translateService.instant(
        'PAYMENTS.SUPER_SUBJECT.CARDS_ONLY'
      )} | `;
    } else if (paypalEcGloballyEnabled) {
      superSubject = `${this.translateService.instant(
        'PAYMENTS.SUPER_SUBJECT.PAYPAL_ONLY'
      )} | `;
    }

    superSubject += this.translateService.instant(
      'PAYMENTS.SUPER_SUBJECT.BALANCE_DUE',
      translateValues
    );

    return superSubject;
  }

  private _buildEmailTemplateExperimentSubject(
    documentNumber: string,
    businessName: string,
    isOverdue: boolean,
    hasDeposit: boolean,
    isDepositOverdue: boolean,
    isFullyPaid: boolean
  ): string {
    const translationParams = {
      documentNumber,
      businessName,
    };

    if (isFullyPaid) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.PAID_SUBJECT',
        translationParams
      );
    }

    if (hasDeposit && isDepositOverdue) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.DEPOSIT_OVERDUE_SUBJECT',
        translationParams
      );
    }

    if (isOverdue) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.OVERDUE_SUBJECT',
        translationParams
      );
    }

    return this.translateService.instant(
      'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.SUBJECT',
      translationParams
    );
  }

  private _buildEmailTemplateExperimentSuperSubject(
    documentDueDate: string,
    depositDueDate: string,
    depositOverdueDays: number,
    isFullyPaid: boolean
  ): string {
    if (isFullyPaid) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.PAID_SUPER_SUBJECT'
      );
    }

    if (depositOverdueDays > 0) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.DEPOSIT_OVERDUE'
      );
    }

    if (depositDueDate) {
      return this.translateService.instant(
        'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.DEPOSIT',
        {
          dueDate: this.friendlyDatePipe.transform(depositDueDate, false),
        }
      );
    }

    return this.translateService.instant(
      'DOCUMENT.INVOICE.EMAIL.EXPERIMENT.DUE',
      {
        dueDate: this.friendlyDatePipe.transform(documentDueDate, false),
      }
    );
  }
}
